import React from "react";

import useTransferWindowStatus from "../../hooks/common/useTransferWindowStatus";

// ──────────────────────────────────────────────────────────────────────────
// One-line status renderer used everywhere a country's transfer-window
// state is shown — Home Quick View, Tender / Requests headers (desktop +
// mobile), any future card. Pure presentation; the data + computation
// live in useTransferWindowStatus.
//
// Layout adapts to the host via two text styles plus a coloured dot:
//
//   <TransferWindowStatusLine country="Spain" />
//   → ● Opens in 63 days · 14 Jul 2026
//
// The Requests pages (where clubs/agents take action) prefer concrete
// DB-backed dates over "expected" predictions, so they pass
// variant="precise-dates":
//
//   <TransferWindowStatusLine country="Spain" variant="precise-dates" />
//   → ● Opens July 1, 2026                           (future window)
//   → ● Open now · Closes February 2, 2026           (currently open)
//   → ● Window closed — check back later             (all known past)
//
// Home Quick View and the redesigned /transfer-windows page keep the
// default behaviour so users still see relative-time hints and the
// "Summer expected July 2026" prediction.
//
// Loading and "no country" states render placeholders so the surrounding
// chrome never collapses.
// ──────────────────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  open:     "#10B981",   // green
  soon:     "#F59E0B",   // amber — opens in ≤14 days
  upcoming: "#F59E0B",   // amber — opens later this period
  expected: "#9CA3AF",   // grey — fallback (no future date in DB)
};

const LONG_DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "long", day: "numeric", year: "numeric",
});
const fmtLong = (d) => (d instanceof Date && !isNaN(d.getTime()) ? LONG_DATE_FMT.format(d) : null);

const MS_PER_DAY = 86400 * 1000;
const daysUntil = (target, now) => {
  if (!(target instanceof Date) || !(now instanceof Date)) return null;
  return Math.max(0, Math.ceil((target.getTime() - now.getTime()) / MS_PER_DAY));
};

// Build the precise-dates line variant from the raw hook output. The
// Requests page is an action surface — users need to know WHEN, not a
// vague "check back later". So:
//
//   open       → "Open · Closes in 5 days (May 31, 2026)"   (countdown
//                 + parenthesised close-date)
//   soon|upcoming → "Closed · Opens July 1, 2026"          ("Closed -"
//                 prefix because right now nothing is open)
//   expected   → "Closed · Opens July 1, 2026 (estimated)" (we use the
//                 hook's deriveFallback prediction so the user sees a
//                 concrete date even when next season hasn't been
//                 imported into the DB yet. FIFA windows repeat
//                 annually so +1 year off the latest same-label window
//                 is a fair guess; we tag it "(estimated)" so it's
//                 visually distinguishable from a DB-backed date.)
//
// Returns { text, estimated } so the caller can render the small (i)
// tooltip next to estimated values.
function precisLine(status, activeWindow, nextWindow, fallback, now) {
  if (status === "open" && activeWindow?.end) {
    const days = daysUntil(activeWindow.end, now);
    const dateStr = fmtLong(activeWindow.end);
    if (days != null && dateStr) {
      return {
        text: `Open · Closes in ${days} day${days === 1 ? "" : "s"} (${dateStr})`,
        estimated: false,
      };
    }
    return {
      text: dateStr ? `Open · Closes ${dateStr}` : "Open now",
      estimated: false,
    };
  }
  if ((status === "soon" || status === "upcoming") && nextWindow?.start) {
    const dateStr = fmtLong(nextWindow.start);
    return {
      text: dateStr ? `Closed · Opens ${dateStr}` : "Closed · Window not yet scheduled",
      estimated: false,
    };
  }
  // Expected fallback: all known windows are in the past. Use the
  // hook's +1y projection if available, else surface a friendlier
  // empty-data message than "TBA". The inline " (est.)" suffix makes
  // the prediction visually distinguishable from a DB-backed date even
  // without hovering the (i) badge.
  if (status === "expected" && fallback?.predictedStart) {
    const dateStr = fmtLong(fallback.predictedStart);
    if (dateStr) {
      return {
        text: `Closed · Opens ${dateStr} (est.)`,
        estimated: true,
      };
    }
  }
  return { text: "Closed · Window not yet scheduled", estimated: false };
}

const TransferWindowStatusLine = ({
  country,
  category = "mens_professional",
  className = "",
  loadingText = "Loading …",
  emptyText = "—",
  showDot = true,
  textColor,
  // "default" → uses the hook's full text (relative time + expected fallback).
  // "precise-dates" → DB-backed long dates with +1y prediction on expected.
  variant = "default",
}) => {
  const { loading, error, status, line, activeWindow, nextWindow, fallback } =
    useTransferWindowStatus(country, category);

  if (loading) {
    return (
      <span className={`inline-flex items-center gap-2 ${className}`} style={textColor ? { color: textColor } : undefined}>
        {loadingText}
      </span>
    );
  }
  if (error || !status) {
    return (
      <span className={`inline-flex items-center gap-2 ${className}`} style={textColor ? { color: textColor } : undefined}>
        {emptyText}
      </span>
    );
  }

  let displayLine = line;
  let estimated = false;
  if (variant === "precise-dates") {
    const out = precisLine(status, activeWindow, nextWindow, fallback, new Date());
    displayLine = out.text;
    estimated = out.estimated;
  }

  const dot = STATUS_COLOR[status] || STATUS_COLOR.expected;
  return (
    <span
      className={`inline-flex items-center gap-2 ${className}`}
      style={textColor ? { color: textColor } : undefined}
      title={
        estimated
          ? `${displayLine} — Estimated based on previous season; exact dates pending FIFA`
          : displayLine
      }
    >
      {showDot && (
        <span
          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
          style={{ background: dot }}
          aria-hidden="true"
        />
      )}
      <span>{displayLine}</span>
      {estimated && (
        <button
          type="button"
          title="Estimated based on previous season; exact dates pending FIFA"
          aria-label="Estimated based on previous season; exact dates pending FIFA"
          className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#E5E7EB] text-[#6B7280] text-[9px] font-bold leading-none flex-shrink-0 hover:bg-[#D1D5DB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9CA3AF] focus-visible:ring-offset-1"
        >
          i
        </button>
      )}
    </span>
  );
};

export default TransferWindowStatusLine;
